import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { API } from "@/lib/api";
import { logError, logDebug } from "@/lib/log";

// Split a raw SSE buffer into parsed JSON events + the unfinished remainder.
function parseSSEEvents(buffer) {
  const parts = buffer.split("\n\n");
  const rest = parts.pop();
  const events = [];
  for (const part of parts) {
    const line = part.replace(/^data:\s?/, "");
    if (!line) continue;
    try {
      events.push(JSON.parse(line));
    } catch (error) {
      // Partial/malformed SSE chunk — expected mid-stream, skip it.
      logDebug("Skipping unparsable SSE chunk:", error?.message);
    }
  }
  return [events, rest];
}

const MessageBubble = ({ message, isStreamingPlaceholder }) => (
  <div
    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
      message.role === "user"
        ? "ml-auto bg-primary text-primary-foreground rounded-br-sm"
        : "bg-muted text-accent rounded-bl-sm"
    }`}
  >
    {message.text || (isStreamingPlaceholder ? "…" : "")}
  </div>
);

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const starters = [
    "I work 9 hours a day — which dogs cope well alone?",
    "Best low-shedding breeds for allergies?",
    "Good first dog for a small apartment?",
  ];
  const [messages, setMessages] = useState([
    { id: 1, role: "assistant", text: "Hey! I'm your dog advisor. Ask me anything — your lifestyle, a breed you're curious about, budget, allergies. What's on your mind?" },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(null);
  const bottomRef = useRef(null);
  const nextIdRef = useRef(2);
  const loc = useLocation();
  const breedMatch = loc.pathname.match(/^\/breeds\/([^/]+)$/);
  const breedContext = breedMatch ? breedMatch[1] : null;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  useEffect(() => {
    const openAdvisor = () => setOpen(true);
    window.addEventListener("open-advisor", openAdvisor);
    return () => window.removeEventListener("open-advisor", openAdvisor);
  }, []);

  const setLastAssistantText = (updater) =>
    setMessages((m) => {
      const copy = [...m];
      const last = copy[copy.length - 1];
      copy[copy.length - 1] = { ...last, text: updater(last.text) };
      return copy;
    });

  const handleEvent = (ev) => {
    if (ev.type === "session") sessionRef.current = ev.session_id;
    else if (ev.type === "delta") setLastAssistantText((t) => t + ev.content);
    else if (ev.type === "error") setLastAssistantText(() => "Sorry, something went wrong. Try again.");
  };

  const send = async (override) => {
    const text = (typeof override === "string" ? override : input).trim();
    if (!text || streaming) return;
    if (typeof override !== "string") setInput("");
    setMessages((m) => [
      ...m,
      { id: nextIdRef.current++, role: "user", text },
      { id: nextIdRef.current++, role: "assistant", text: "" },
    ]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionRef.current,
          message: text,
          breed_context: breedContext,
        }),
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const [events, rest] = parseSSEEvents(buffer);
        buffer = rest;
        events.forEach(handleEvent);
      }
    } catch (error) {
      logError("Chat stream failed:", error);
      setLastAssistantText(() => "Network error. Please try again.");
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      {/* Toggle */}
      <button
        data-testid="chat-toggle-btn"
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 pill-btn rounded-full bg-primary text-primary-foreground shadow-[0_12px_40px_-12px_rgba(224,122,95,0.55)] px-5 py-3 font-semibold inline-flex items-center gap-2"
      >
        {open ? <X size={18} /> : <MessageCircle size={18} />}
        <span className="hidden sm:inline">{open ? "Close" : "Ask advisor"}</span>
      </button>

      {open && (
        <div
          data-testid="chat-window"
          className="fixed bottom-24 right-6 z-40 w-[calc(100vw-3rem)] sm:w-[400px] h-[560px] max-h-[70vh] rounded-3xl bg-card border border-border/60 warm-shadow flex flex-col overflow-hidden"
        >
          <div className="p-4 border-b border-border/60 bg-secondary/50 flex items-center gap-3">
            <span className="w-9 h-9 rounded-full bg-primary text-primary-foreground grid place-items-center">
              <Sparkles size={16} />
            </span>
            <div>
              <div className="font-display font-bold text-accent">Dog Advisor</div>
              <div className="text-xs text-muted-foreground">Powered by Claude · Not veterinary advice</div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <MessageBubble
                key={m.id}
                message={m}
                isStreamingPlaceholder={streaming && i === messages.length - 1}
              />
            ))}
            {messages.length === 1 && !streaming && (
              <div className="pt-1 space-y-2" data-testid="chat-starters">
                <p className="text-xs text-muted-foreground font-medium">Try asking…</p>
                {starters.map((q) => (
                  <button
                    key={q}
                    type="button"
                    data-testid="chat-starter"
                    onClick={() => send(q)}
                    className="block w-full text-left rounded-2xl border border-border/60 bg-background hover:bg-secondary/60 hover:border-primary/50 transition-colors px-3.5 py-2.5 text-sm text-accent"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="p-3 border-t border-border/60 flex gap-2"
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about breeds, care, cost…"
              disabled={streaming}
              className="flex-1 rounded-full bg-muted px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/40"
            />
            <button
              data-testid="chat-send-btn"
              type="submit"
              disabled={streaming || !input.trim()}
              className="w-11 h-11 rounded-full bg-primary text-primary-foreground grid place-items-center disabled:opacity-40 hover:bg-primary/90 transition-colors"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
